/**
 * MCP 接入（我的 › MCP 接入，docs/MCP.md）：可直接复制的 JSON 配置 / Claude Code 命令，长期访问令牌（kpat_…）的新建与吊销，
 * 通过 OAuth 连接的应用（断开）。接口：GET mcp/info、POST mcp/tokens（明文 token 只返回这一次）、DELETE mcp/tokens/{id}、DELETE mcp/connections/{id}。
 */
import { ChevronLeft, Copy, KeyRound, Plug, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ago, api, confirm, run, useCalendar } from "../../core";
import { Button, ChoiceChip, EmptyHint, IconButton, Page, PageTitle, Pill, PillSegment, RowDivider, SectionLabel, Spinner, TextField, C } from "../../ui";
import { copyText } from "./util";
import "./mcp.css";

interface McpToken {
  id: string;
  name: string;
  prefix: string;
  write: boolean;
  expiresAt: number | null;
  createdAt: number;
  lastUsedAt: number | null;
}

interface McpConnection {
  id: string;
  name: string;
  write: boolean;
  createdAt: number;
}

interface McpInfo {
  url: string;
  tokens: McpToken[];
  connections: McpConnection[];
}

/** 刚创建的令牌（明文只在这次显示） */
interface NewToken {
  id: string;
  name: string;
  token: string;
}

const TOKEN_PLACEHOLDER = "kpat_…";
const EXPIRY_CHOICES: readonly [number | null, string][] = [
  [30, "30 天"],
  [90, "90 天"],
  [365, "365 天"],
  [null, "永不过期"],
];

export function oauthConfig(url: string): string {
  return JSON.stringify({ mcpServers: { "kids-planner": { type: "http", url } } }, null, 2);
}

export function tokenConfig(url: string, token: string): string {
  return JSON.stringify({ mcpServers: { "kids-planner": { type: "http", url, headers: { Authorization: `Bearer ${token}` } } } }, null, 2);
}

export function McpPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<McpInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<NewToken | null>(null);

  const load = useCallback(async () => {
    try {
      setInfo(await api.get<McpInfo>("mcp/info"));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const url = info?.url ?? "";

  return (
    <Page gap={14}>
      <div className="mcp-top">
        <IconButton label="返回我的" onClick={() => navigate("/me")}>
          <ChevronLeft size={22} strokeWidth={2.6} />
        </IconButton>
        <PageTitle>MCP 接入</PageTitle>
      </div>
      <p className="mcp-intro">让 Claude 等 AI 助手通过 MCP 查看、安排孩子的日程、学习和积分。把下面的配置填到 AI 客户端里就能用。</p>

      {!info ? (
        error ? (
          <EmptyHint icon={TriangleAlert} text={error}>
            <Button variant="dark" height={44} onClick={() => void load()}>
              重试
            </Button>
          </EmptyHint>
        ) : (
          <div className="mcp-loading">
            <Spinner size={24} />
          </div>
        )
      ) : (
        <>
          <section className="mcp-card">
            <div className="mcp-url">
              <span>MCP 地址</span>
              <code>{url}</code>
              <IconButton label="复制 MCP 地址" size={36} onClick={() => void copyText(url, "地址已复制")}>
                <Copy size={16} />
              </IconButton>
            </div>
          </section>

          <div className="mcp-grid">
            <section className="mcp-card">
              <h2>
                <Plug size={18} /> OAuth 登录（推荐）
              </h2>
              <p className="mcp-desc">适用于支持 OAuth 的客户端：claude.ai、Claude Desktop、Claude Code、Cursor 等。添加后第一次使用时会打开浏览器登录授权，不用保存令牌。</p>
              <CodeBlock label="JSON 配置" code={oauthConfig(url)} />
              <CodeBlock label="Claude Code 命令" code={`claude mcp add --transport http kids-planner ${url}`} />
            </section>

            <section className="mcp-card">
              <h2>
                <KeyRound size={18} /> 访问令牌
              </h2>
              <p className="mcp-desc">适用于不支持 OAuth 的客户端或脚本：在下面新建令牌，填到请求头 Authorization 里。{created ? "下面的配置已填好刚创建的令牌。" : `配置里的 ${TOKEN_PLACEHOLDER} 换成你的令牌。`}</p>
              {created && <CreatedToken token={created} onDismiss={() => setCreated(null)} />}
              <CodeBlock label="JSON 配置" code={tokenConfig(url, created?.token ?? TOKEN_PLACEHOLDER)} />
              <CodeBlock label="Claude Code 命令" code={`claude mcp add --transport http kids-planner ${url} --header "Authorization: Bearer ${created?.token ?? TOKEN_PLACEHOLDER}"`} />
            </section>
          </div>

          <CreateTokenForm
            onCreated={(t) => {
              setCreated(t);
              void load();
            }}
          />
          <TokenList tokens={info.tokens} createdId={created?.id ?? null} onChanged={() => void load()} onRevoked={(id) => created?.id === id && setCreated(null)} />
          <ConnectionList connections={info.connections} onChanged={() => void load()} />
        </>
      )}
    </Page>
  );
}

function CodeBlock({ label, code }: { label: string; code: string }) {
  return (
    <div className="mcp-code">
      <div className="mcp-code-head">
        <span>{label}</span>
        <button type="button" onClick={() => void copyText(code)}>
          <Copy size={14} /> 复制
        </button>
      </div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function CreatedToken({ token, onDismiss }: { token: NewToken; onDismiss: () => void }) {
  return (
    <div className="mcp-created" role="status">
      <strong>
        <TriangleAlert size={16} /> 令牌「{token.name}」只显示这一次，请马上复制保存
      </strong>
      <div className="mcp-created-token">
        <code>{token.token}</code>
        <IconButton label="复制令牌" size={36} onClick={() => void copyText(token.token, "令牌已复制")}>
          <Copy size={16} />
        </IconButton>
      </div>
      <button type="button" className="mcp-link-btn" onClick={onDismiss}>
        我已保存，隐藏令牌
      </button>
    </div>
  );
}

function CreateTokenForm({ onCreated }: { onCreated: (t: NewToken) => void }) {
  const [name, setName] = useState("");
  const [write, setWrite] = useState(true);
  const [days, setDays] = useState<number | null>(90);
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();

  async function submit() {
    if (!trimmed) return;
    setBusy(true);
    const r = await run(() => api.post<{ id: string; name: string; token: string }>("mcp/tokens", { name: trimmed, write, expiresDays: days }), "令牌已创建");
    setBusy(false);
    if (!r) return;
    setName("");
    onCreated({ id: r.id, name: r.name, token: r.token });
  }

  return (
    <section className="mcp-card">
      <h2>新建访问令牌</h2>
      <form
        className="mcp-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField label="名称" placeholder="例如：Claude Desktop" value={name} onChange={setName} maxLength={40} />
        <div className="mcp-form-row">
          <span className="kp-field-label">权限</span>
          <PillSegment
            items={[
              [1, "查看 + 修改"],
              [0, "只读"],
            ]}
            value={write ? 1 : 0}
            onChange={(v) => setWrite(v === 1)}
            background="var(--kp-bg)"
          />
        </div>
        <div className="mcp-form-row">
          <span className="kp-field-label">有效期</span>
          <div className="mcp-chips">
            {EXPIRY_CHOICES.map(([d, label]) => (
              <ChoiceChip key={String(d)} selected={days === d} onClick={() => setDays(d)}>
                {label}
              </ChoiceChip>
            ))}
          </div>
        </div>
        <Button type="submit" variant="dark" height={48} busy={busy} disabled={!trimmed} className="mcp-submit">
          创建令牌
        </Button>
      </form>
    </section>
  );
}

function TokenList({ tokens, createdId, onChanged, onRevoked }: { tokens: McpToken[]; createdId: string | null; onChanged: () => void; onRevoked: (id: string) => void }) {
  const cal = useCalendar();
  const now = Date.now();

  async function revoke(t: McpToken) {
    const ok = await confirm({ title: `吊销令牌「${t.name}」？`, message: "用这个令牌的客户端会立即失去访问权限。", confirmText: "吊销", danger: true });
    if (!ok) return;
    const r = await run(() => api.del(`mcp/tokens/${t.id}`), "已吊销");
    if (r === undefined) return;
    onRevoked(t.id);
    onChanged();
  }

  return (
    <section className="mcp-card">
      <SectionLabel>访问令牌</SectionLabel>
      {tokens.length === 0 && <p className="mcp-empty">还没有访问令牌</p>}
      {tokens.map((t, i) => {
        const expired = t.expiresAt != null && t.expiresAt <= now;
        return (
          <div key={t.id}>
            {i > 0 && <RowDivider />}
            <div className="mcp-item">
              <div className="mcp-item-text">
                <strong>
                  {t.name}
                  <Pill tint={t.write ? C.accentSoft : C.chip} ink={t.write ? C.gold : C.inkSoft} size={11}>
                    {t.write ? "查看 + 修改" : "只读"}
                  </Pill>
                  {t.id === createdId && (
                    <Pill tint={C.successSoft} ink="var(--kp-other-ink)" size={11}>
                      刚创建
                    </Pill>
                  )}
                </strong>
                <code>{t.prefix}…</code>
                <small>
                  {cal.dateString(t.createdAt)} 创建 · {t.expiresAt == null ? "永不过期" : expired ? <em>已过期</em> : `${cal.dateString(t.expiresAt)} 过期`} ·{" "}
                  {t.lastUsedAt == null ? "从未使用" : `${ago(t.lastUsedAt)}使用`}
                </small>
              </div>
              <button type="button" className="mcp-danger-btn" onClick={() => void revoke(t)}>
                吊销
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function ConnectionList({ connections, onChanged }: { connections: McpConnection[]; onChanged: () => void }) {
  const cal = useCalendar();

  async function disconnect(c: McpConnection) {
    const ok = await confirm({ title: `断开「${c.name}」？`, message: "这个应用需要重新登录授权才能再访问。", confirmText: "断开", danger: true });
    if (!ok) return;
    const r = await run(() => api.del(`mcp/connections/${c.id}`), "已断开");
    if (r !== undefined) onChanged();
  }

  return (
    <section className="mcp-card">
      <SectionLabel>已连接的应用（OAuth）</SectionLabel>
      {connections.length === 0 && <p className="mcp-empty">还没有通过 OAuth 连接的应用</p>}
      {connections.map((c, i) => (
        <div key={c.id}>
          {i > 0 && <RowDivider />}
          <div className="mcp-item">
            <div className="mcp-item-text">
              <strong>
                {c.name}
                <Pill tint={c.write ? C.accentSoft : C.chip} ink={c.write ? C.gold : C.inkSoft} size={11}>
                  {c.write ? "查看 + 修改" : "只读"}
                </Pill>
              </strong>
              <small>{cal.dateString(c.createdAt)} 连接</small>
            </div>
            <button type="button" className="mcp-danger-btn" onClick={() => void disconnect(c)}>
              断开
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}
